import { Brand } from "./Brand";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../features/auth/AuthProvider";
import { useUnreadCount } from "../features/notifications/hooks";
import { useRealtime } from "../lib/realtime";
import { getTheme, toggleTheme, type Theme } from "../lib/theme";

export function Layout() {
  const { user, logout } = useAuth();
  const unread = useUnreadCount();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [theme, setThemeState] = useState<Theme>(getTheme);
  const { pathname } = useLocation();
  const qc = useQueryClient();

  useEffect(() => setDrawerOpen(false), [pathname]);

  useRealtime((event) => {
    if (event.type === "price.updated") {
      void qc.invalidateQueries({ queryKey: ["portfolios"] });
      void qc.invalidateQueries({ queryKey: ["positions"] });
    } else if (event.type === "opportunity.published") {
      void qc.invalidateQueries({ queryKey: ["opportunities"] });
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    }
  });

  function handleToggleTheme() {
    setThemeState(toggleTheme());
  }

  return (
    <div className="app-shell">
      <header className="mobile-topbar">
        <button
          type="button"
          className="hamburger"
          aria-label="Ouvrir le menu"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>
        <Brand />
        <div className="topbar-actions">
          <Link to="/notifications" className="notif-bell" aria-label="Notifications">
            🔔
            {unread > 0 && <span className="nav-badge">{unread}</span>}
          </Link>
          <Link to="/simulator" className="notif-bell" aria-label="Simulateur" title="Simulateur">
            🧮
          </Link>
          <button
            type="button"
            className="theme-toggle"
            aria-label={theme === "dark" ? "Passer en thème clair" : "Passer en thème sombre"}
            onClick={handleToggleTheme}
          >
            {theme === "dark" ? "☀️" : "🌙"}
          </button>
        </div>
      </header>

      {drawerOpen && <div className="sidebar-backdrop" onClick={() => setDrawerOpen(false)} />}

      <aside className={`sidebar ${drawerOpen ? "sidebar-open" : ""}`}>
        <Brand className="sidebar-brand" />
        <nav>
          <NavLink to="/portfolios" className={({ isActive }) => (isActive ? "active" : "")}>
            Portefeuilles
          </NavLink>
          <NavLink to="/feed" className={({ isActive }) => (isActive ? "active" : "")}>
            Actualités
          </NavLink>
          <NavLink to="/notifications" className={({ isActive }) => (isActive ? "active" : "")}>
            Notifications
            {unread > 0 && <span className="nav-badge">{unread}</span>}
          </NavLink>
          <NavLink to="/simulator" className={({ isActive }) => (isActive ? "active" : "")}>
            Simulateur
          </NavLink>
        </nav>
        <div className="sidebar-foot">
          <span title={user?.email}>{user?.email}</span>
          <button className="btn btn-ghost" onClick={handleToggleTheme}>
            {theme === "dark" ? "☀️ Thème clair" : "🌙 Thème sombre"}
          </button>
          <button className="btn btn-ghost" onClick={logout}>
            Déconnexion
          </button>
        </div>
      </aside>
      <main className="content">
        <Outlet />
      </main>
    </div>
  );
}
